<template>
  <div :style="{width}" :class="useRadioButtons ? 'radio-state-input' : 'state-input'">
    <label v-if="showLabel" :class="`${color} color`">{{ state.name[language] }}</label>
    <md-field class="form-item">
      <span v-if="$slots.prefix" class="md-prefix">
        <slot name="prefix" />
      </span>
      <md-input
        v-if="state.type === 'string'"
        :aria-label="state.name[language]"
        :disabled="disabled"
        class="state-input"
        :value="value"
        @input="value => update(value.trim())"
      >
        <template v-for="slot in Object.keys($slots)" :slot="slot">
          <slot :name="slot" />
        </template>
      </md-input>
      <md-input
        v-if="state.type === 'number'"
        :aria-label="state.name[language]"
        type="number"
        :disabled="disabled"
        :class="{ 'error': hasError }"
        class="state-input number-input"
        :value="value"
        :min="isAbsolute ? 0 : undefined"
        :max="state.maxValue"
        @input="value => update(parseFloat(value) || 0)"
      >
        <template v-for="slot in Object.keys($slots)" :slot="slot">
          <slot :name="slot" />
        </template>
      </md-input>
      <span 
        v-if="state.type === 'number' && state.units"
        class="md-suffix"
      >
        {{ state.units }}
      </span>
      <md-select
        v-if="state.type === 'enum' && !useRadioButtons"
        ref="select"
        v-close-on-zoom
        :aria-label="state.name[language]"
        :disabled="disabled"
        :multiple="multiple"
        class="state-input"
        :value="value"
        :placeholder="placeholder || state.name[language]"
        @input="value => update(value)"
      >
        <md-option
          v-for="enumValue in orderBy(state.possibleValues, `label.${language}`)"
          :key="enumValue.key"
          :value="enumValue.key"
          class="enum-option"
          :class="{'with-picture': !!enumValue.picture}"
        >
          <!-- Vue material expects to find some text as the first child of the md-option -->
          <!-- Otherwise it throws an error (cannot read property trim of undefined) -->
          {{ '' }}
          <div class="content">
            <span class="enum-text">{{ enumValue.label[language] }}</span>
            <img v-if="!!enumValue.picture" :src="enumValue.picture.url" class="enum-picture">
          </div>
        </md-option>
      </md-select>
      <div v-if="state.type === 'enum' && useRadioButtons" class="radio-item-container">
        <radio-buttons
          :options="getRadioOptions()"
          :value="value" 
          :disabled="disabled"
          :aria-label="state.name[language]"
          active-color="blue-4"
          inactive-color="white"
          :bordered="true"
          @input="value => update(value)"
        />
      </div>
    </md-field>
  </div>
</template>

<script>
import isUndefined from 'lodash/isUndefined';
import orderBy from 'lodash/orderBy';
import debounce from 'lodash/debounce';
import RadioButtons from './RadioButtons';

export default {
  name: 'state-input',
  components: {
    RadioButtons,
  },
  props: {
    state: {
      type: Object,
      required: true,
    },
    language: {
      type: String,
      default: 'en',
    },
    initialValue: {
      type: [String, Number, Array],
      default: undefined,
    },
    syncedValue: {
      type: [String, Number, Array],
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    useDefault: {
      type: Boolean,
      default: false,
    },
    isAbsolute: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: null,
    },
    color: {
      type: String,
      default: 'gray-7',
    },
    useRadioButtons: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      hasError: false,
      value: this.initialValue,
    };
  },
  watch: {
    syncedValue(newValue) {
      this.update(newValue);
    },
  },
  created() {
    this.showError = debounce(this.showError, 1000);

    this.value = this.initialValue;
    if (isUndefined(this.value)) {
      this.value = this.default();
      this.update(this.default());
    }
  },
  mounted() {
    this.setSelectLabel(this.value);
  },
  methods: {
    showError() {
      this.hasError = true;
    },
    update(value) {
      // There is a bug in vue material where the value
      // will not sync back to the input if it does not
      // change, so we have to accept the potentially erroneous
      // value before fixing it in the next render tick
      this.value = value;
      this.$nextTick(() => {
        if (this.state.type === 'number') {
          this.value = this.rangeLimited(value);
        }
        this.emitUpdate(this.value);
        this.setSelectLabel(this.value);
      });
    },
    emitUpdate(value) {
      this.$emit('input', value);
    },
    default() {
      const { state } = this;
      if (this.useDefault && state.defaultValue !== null) {
        return state.defaultValue;
      }
      const defaultMap = {
        number: 0,
        string: '',
        enum:
          this.useDefault && state.possibleValues
            ? state.possibleValues[0].key
            : null,
      };
      return defaultMap[state.type];
    },
    rangeLimited(value) {
      if (this.state.type !== 'number') {
        return value;
      }
      const { state } = this;
      let rangeLimitedValue = value;
      if (state.maxValue) {
        // Clamp the value in the range [-maxValue, maxValue]
        // the -maxValue is in case this is used for relative
        // values (e.g. in StateEffect)
        rangeLimitedValue = Math.min(rangeLimitedValue, state.maxValue);
        rangeLimitedValue = Math.max(rangeLimitedValue, -state.maxValue);
      }
      if (this.isAbsolute) {
        rangeLimitedValue = Math.max(rangeLimitedValue, 0);
      }
      return rangeLimitedValue;
    },
    // With non textual md-select option content
    // the default value is not input when the
    // md-select mounts
    setSelectLabel(value = this.value) {
      if (!this.$refs.select || !this.$refs.select.$el) {
        return;
      }
      const selectedEnum = this.state.possibleValues.find(
        enumValue => enumValue.key === value,
      );
      if (!selectedEnum) {
        return;
      }
      setTimeout(() => {
        if (this.$refs.select && this.$refs.select.$el) {
          this.$refs.select.$el.children[0].value = selectedEnum.label[this.language];
        }
      }, 50);
    },
    orderBy,
    getRadioOptions() {
      return this.state.possibleValues.map(val => ({
        value: val.key,
        label: val.label[this.language],
      }));
    },
  },
};
</script>

<style scoped>

.number-input {
  width: 100%;
}

.form-item {
  margin-bottom: 0;
}

.md-select {
  width: auto;
  max-width: none;
}

.enum-option .content {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.enum-text {
  width: auto;
}

.enum-picture {
  height: 36px;
  margin-left: 5px;
  border-radius: 6px;
  width: auto;
}

.radio-state-input {
  margin-bottom: 20px;
}
.radio-state-input > label {
  margin-top: 10px;
  margin-bottom: 10px;
  display: block;
}
label {
  text-transform: capitalize;
}

.md-prefix {
  color: #fff !important;
  display: block !important;
}
.md-suffix {
  right: 12px;
  position: absolute;
}
</style>
